import { useState } from 'react'
import { isBatchExpiredOrExpiring } from '../utils/batchUtils'
import { Batch } from '../../../interfaces/Batch'
import { eTypePosition } from '../../../enums/eTypePosition'
import { toBaseUnits, toDunPacks } from '../utils/dunUtils'

interface UseQuantityValidationProps {
  sharedState: {
    selectedBatch?: Batch
    availableStock?: number
    originZone?: any
    originPositionType?: number
    cantidad?: number | null
    expirationDays?: number
    isDun?: boolean
    dunQty?: number
    typePackageName?: string
    cantidadDun?: number | null
  }
  setSharedState: (updater: (prev: any) => any) => void
  onQuantityValidated: () => void
  onShowMermaQuestion: () => void
  onAutoSendToQuality: (isDamaged: boolean) => Promise<void>
}

interface UseQuantityValidationReturn {
  localQuantity: string
  setLocalQuantity: (value: string) => void
  quantityValidated: boolean
  setQuantityValidated: (value: boolean) => void
  error: string
  setError: (value: string) => void
  handleQuantityChange: (value: string) => void
  handleValidateQuantity: () => void
  handleEditQuantity: () => void
}

export const useQuantityValidation = ({
  sharedState,
  setSharedState,
  onQuantityValidated,
  onShowMermaQuestion,
  onAutoSendToQuality
}: UseQuantityValidationProps): UseQuantityValidationReturn => {
  // Inicializar con valores existentes de sharedState (para cuando se vuelve desde
  // ConfirmationStep). En modo DUN el input muestra cajas, no unidades.
  const initialQuantity = sharedState.isDun
    ? sharedState.cantidadDun
    : sharedState.cantidad
  const [localQuantity, setLocalQuantity] = useState(initialQuantity ? String(initialQuantity) : '')
  const [quantityValidated, setQuantityValidated] = useState(Boolean(sharedState.cantidad))
  const [error, setError] = useState('')

  const handleQuantityChange = (value: string) => {
    setLocalQuantity(value)
    setQuantityValidated(false)
    setError('')
  }

  const handleValidateQuantity = async () => {
    const numValue = parseInt(localQuantity) || 0

    // Determinar stock máximo disponible según FEFO (siempre en unidades base)
    const maxUnits = sharedState.selectedBatch
      ? sharedState.selectedBatch.quantity
      : sharedState.availableStock

    // En modo DUN el operador cuenta cajas: el tope y el valor tipeado están en
    // cajas completas, y se traducen a unidades base antes de guardarlos.
    const maxQuantity = sharedState.isDun
      ? toDunPacks(maxUnits || 0, sharedState.dunQty)
      : maxUnits

    if (numValue > 0 && numValue <= (maxQuantity || 0)) {
      setSharedState(prev => ({
        ...prev,
        cantidad: sharedState.isDun
          ? toBaseUnits(numValue, sharedState.dunQty)
          : numValue,
        cantidadDun: sharedState.isDun ? numValue : null
      }))
      setQuantityValidated(true)
      setError('')

      // Verificar si lote está vencido o por vencer
      const shouldAutoSendToQuality = isBatchExpiredOrExpiring(
        sharedState.selectedBatch,
        sharedState.expirationDays || 0
      )

      if (shouldAutoSendToQuality) {
        // Auto-enviar a calidad sin preguntar
        await onAutoSendToQuality(true)
        return
      }

      // Mostrar pregunta de merma solo si la posición de origen es de tipo Venta y tiene zona
      if (
        sharedState.originPositionType === eTypePosition.Venta &&
        sharedState.originZone !== null &&
        sharedState.originZone !== undefined
      ) {
        onShowMermaQuestion()
        return
      } else {
        // Si no tiene zona, continuar con el flujo normal
        onQuantityValidated()
      }
    } else if (numValue > (maxQuantity || 0)) {
      const context = sharedState.selectedBatch ? 'del lote seleccionado' : 'disponible'
      const unit = sharedState.isDun
        ? ` ${sharedState.typePackageName || 'Piezas agrupadas'}`
        : ''
      setError(`Cantidad no puede ser mayor a ${maxQuantity}${unit} ${context}`)
      setQuantityValidated(false)
    } else {
      setError('Ingrese una cantidad válida mayor a 0')
      setQuantityValidated(false)
    }
  }

  const handleEditQuantity = () => {
    setQuantityValidated(false)
    setSharedState(prev => ({ ...prev, cantidad: null, cantidadDun: null }))
    setError('')
  }

  return {
    localQuantity,
    setLocalQuantity,
    quantityValidated,
    setQuantityValidated,
    error,
    setError,
    handleQuantityChange,
    handleValidateQuantity,
    handleEditQuantity
  }
}